import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';

const containerStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1.5)};
`;

// Matches the Figma track: 1px 10% black stroke plus two stacked inner shadows in the
// --fabric-color-shadow-light-* navy at 15% (no token exists at that opacity).
const trackShadowColor = 'rgb(0 33 82 / 15%)';

const trackStyle = css`
  flex: 1;
  height: 13px;
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.full};
  box-shadow: inset 0 0 0 1px rgb(0 0 0 / 10%), inset 0 1px 1px ${trackShadowColor},
    inset 0 1px 3px 1px ${trackShadowColor};
  overflow: hidden;
`;

const fillStyle = css`
  height: 100%;
  background-color: ${theme.colors.primary50};
  border-radius: ${theme.borderRadius.full};
  transition: width 300ms ease-in-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const labelStyle = css`
  margin: 0;
  flex-shrink: 0;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

interface SectionProgressProps {
  /** 1-based index of the section the patient is on. */
  current: number;
  total: number;
}

export const SectionProgress = ({ current, total }: SectionProgressProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const safeTotal = Math.max(total, 1);
  const safeCurrent = Math.min(Math.max(current, 1), safeTotal);
  const percentage = (safeCurrent / safeTotal) * 100;

  return (
    <div css={containerStyle} data-testid='section-progress'>
      <div
        role='progressbar'
        aria-label={t('Form progress')}
        aria-valuemin={1}
        aria-valuemax={safeTotal}
        aria-valuenow={safeCurrent}
        aria-valuetext={t('Page {{current}} of {{total}}', { current: safeCurrent, total: safeTotal })}
        css={trackStyle}
      >
        <div css={fillStyle} style={{ width: `${percentage}%` }} data-testid='section-progress-fill' />
      </div>
      <Text size='small' color='subdued' css={labelStyle} aria-hidden>
        {t('{{current}}/{{total}} Pages', { current: safeCurrent, total: safeTotal })}
      </Text>
    </div>
  );
};
